import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Leftsidebar from "../sidebar/leftsidebar";
import LeftResponsivesidebar from "../sidebar/left-responsive";
import Headmenu from "../sidebar/headmenu";
import CreateAdCampaignObjective from "./CreateAdCampaignObjective";
import CreateAdSet from "./CreateAdSet";
import AdBudgetAllocation from "./AdBudgetAllocation";
import CreateAd from "./CreateAd";
import AdPlacement from "./AdPlacement";
import AdFinalPreview from "./AdFinalPreview";
import { AGE_MIN, AGE_MAX, soundIdFromSearch, getCurrentUserId } from "./adsConstants";
import { getBillingProfile } from "../../hook/useAdsApi";
import progressFilledIcon from "../../assets/images/icons/ads-progress-filled.svg";
import progressProcessIcon from "../../assets/images/icons/ads-progress-process.svg";
import progressPendingIcon from "../../assets/images/icons/ads-progress-pending.svg";

const STEP_TITLES = [
  "Create New Ad Campaign",
  "Budget Allocation",
  "Ad Set",
  "Create New Ad",
  "Ad Placement",
  "Ad Final Preview",
];
const TOTAL_STEPS = STEP_TITLES.length;

const INITIAL_FORM_DATA = {
  campaignName: "",
  objective: "",
  adSetName: "",
  targetType: "profile",
  targetId: "",
  gender: "all",
  minAge: AGE_MIN,
  maxAge: AGE_MAX,
  interests: [],
  devices: "all",
  connectToSeller: false,
  previousBrandBuyers: false,
  budgetType: "daily",
  budget: 0,
  startDate: "",
  endDate: "",
  adName: "",
  creativeType: "image",
  mediaFiles: [],
  thumbnail: null,
  headline: "",
  description: "",
  cta: "",
  destinationType: "profile",
  destinationId: "",
  placements: { searchResult: false, marketplaceFeed: false, categoryResults: false },
  soundId: "", // set when the wizard was opened from "Promote this sound"
};

export default function CreateAdCampaign() {
  const { state, pathname, search } = useLocation();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState(() => {
    const soundId = soundIdFromSearch(search);
    if (!soundId) return INITIAL_FORM_DATA;
    // Opened from an artist's "Promote this sound" button: start on the sound objective with the track set.
    return {
      ...INITIAL_FORM_DATA,
      soundId,
      objective: "sound_promotions",
      destinationType: "track",
      destinationId: soundId,
      targetType: "profile",
      targetId: getCurrentUserId(),
    };
  });
  // Checked here (not just the one-time nudge in AddAdAccount.js right after account creation)
  // because a seller can land on this screen without ever completing that nudge - an existing
  // account, a skipped setup, a bookmarked link. Without this, the whole wizard was fillable
  // start to finish with no billing profile at all, only failing later at Activate.
  const [billingLoading, setBillingLoading] = useState(true);
  const [billingConfigured, setBillingConfigured] = useState(true);

  const adAccount = state?.adAccount;

  useEffect(() => {
    getBillingProfile()
      .then((res) => setBillingConfigured(Boolean(res?.configured)))
      .catch(() => setBillingConfigured(true))
      .finally(() => setBillingLoading(false));
  }, []);

  if (!adAccount) {
    // Keep the Promote this sound link so the wizard can pick it up after the account is chosen.
    navigate("/ads/select-account", { replace: true, state: soundIdFromSearch(search) ? { next: `${pathname}${search}` } : undefined });
    return null;
  }

  if (billingLoading) {
    return (
      <div className="dashboard w-full min-h-screen flex">
        <Leftsidebar />
        <main className="flex-1 min-w-0 p-4 md:p-6 md:pl-8">
          <LeftResponsivesidebar />
          <Headmenu />
          <p className="text-sm text-gray-500">Loading...</p>
        </main>
      </div>
    );
  }

  if (!billingConfigured) {
    return (
      <div className="dashboard w-full min-h-screen flex">
        <Leftsidebar />
        <main className="flex-1 min-w-0 p-4 md:p-6 md:pl-8">
          <LeftResponsivesidebar />
          <Headmenu />
          <main className="flex">
            <div className="w-full max-w-2xl">
              <div className="rounded-2xl border-[1.5px] border-[#39B54A] p-8 text-center bg-[#eafcf3]">
                <h2 className="text-lg font-bold text-[#095228] mb-2">Set up billing to create ads</h2>
                <p className="text-sm text-gray-600 mb-5">
                  You need to set up a billing model (Prepaid or Postpaid) before you can create and run ad campaigns.
                </p>
                <button
                  type="button"
                  onClick={() => navigate("/ads/billing/model?mode=setup", { state: { adAccount } })}
                  className="bg-[#095228] text-white font-semibold px-6 py-2.5 rounded-full text-sm hover:opacity-90 transition"
                >
                  Set Up Billing →
                </button>
              </div>
            </div>
          </main>
        </main>
      </div>
    );
  }

  const updateFormData = (patch) => setFormData((prev) => ({ ...prev, ...patch }));
  const goNext = () => setStep((s) => Math.min(TOTAL_STEPS, s + 1));
  const goBack = () => setStep((s) => Math.max(1, s - 1));

  const handleSubmitted = ({ ad, campaign }) => {
    navigate("/ads/submitted-success", { state: { ad, campaign, adAccount } });
  };

  return (
    <div className="dashboard w-full min-h-screen flex">
      <Leftsidebar />
      <main className="flex-1 min-w-0 p-4 md:p-6 md:pl-8">
        <LeftResponsivesidebar />
        <Headmenu />
        <main className="flex">
          <div className="w-full max-w-2xl">
            <div className="flex items-center gap-3 mb-2">
              <button onClick={() => (step === 1 ? navigate(-1) : goBack())} className="rounded-full bg-[#e7f3ea] p-2">
                ←
              </button>
              <h1 className="text-[22px] font-semibold text-black capitalize">{STEP_TITLES[step - 1]}</h1>
            </div>
            <div className="w-full h-1 bg-[#d9d9d9] rounded-full mb-4" />

            {/* Exact assets from Figma's step-progress indicator (read-only access):
                Filled.svg (done, #095228), Process.svg (current, #98911f), Pending.svg
                (upcoming, #d9d9d9); "STEP" black / "X OF Y" green. */}
            <div className="flex items-center justify-between mb-6">
              <p className="text-sm font-bold uppercase tracking-[1.1px]">
                <span className="text-black">STEP</span> <span className="text-[#095228]">{step} OF {TOTAL_STEPS}</span>
              </p>
              <div className="flex gap-2 flex-1 max-w-[340px] ml-6">
                {STEP_TITLES.map((_, i) => (
                  <img
                    key={i}
                    src={i < step - 1 ? progressFilledIcon : i === step - 1 ? progressProcessIcon : progressPendingIcon}
                    alt=""
                    className="h-[7px] flex-1 min-w-0"
                  />
                ))}
              </div>
            </div>

            {step === 1 && (
              <CreateAdCampaignObjective formData={formData} updateFormData={updateFormData} onNext={goNext} />
            )}
            {step === 2 && (
              <AdBudgetAllocation formData={formData} updateFormData={updateFormData} onNext={goNext} onBack={goBack} />
            )}
            {step === 3 && (
              <CreateAdSet
                formData={formData}
                updateFormData={updateFormData}
                onNext={goNext}
                onBack={goBack}
                adAccount={adAccount}
              />
            )}
            {step === 4 && (
              <CreateAd formData={formData} updateFormData={updateFormData} onNext={goNext} onBack={goBack} adAccount={adAccount} />
            )}
            {step === 5 && (
              <AdPlacement formData={formData} updateFormData={updateFormData} onNext={goNext} onBack={goBack} />
            )}
            {step === 6 && (
              <AdFinalPreview formData={formData} adAccount={adAccount} onBack={goBack} onSubmitted={handleSubmitted} />
            )}
          </div>
        </main>
      </main>
    </div>
  );
}
